import { DEFAULT_RADIUS } from '../helpers';
import { useCityInfo } from './useCityInfo';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';

export type UseAutocompleteSuggestionsReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  isLoading: boolean;
  resetSession: () => void;
};

export const useAutocompleteSuggestions = (
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
  cityCenter?: { latitude?: string; longitude?: string },
): UseAutocompleteSuggestionsReturn => {
  const placesLib = useMapsLibrary('places');
  const { getCityRadiusFromCoordinates } = useCityInfo();
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dynamicRadius, setDynamicRadius] = useState<number | null>(null);

  useEffect(() => {
    if (cityCenter?.latitude && cityCenter?.longitude) {
      getCityRadiusFromCoordinates(Number(cityCenter.latitude), Number(cityCenter.longitude)).then((radius) => {
        setDynamicRadius(radius);
      });
    } else {
      setDynamicRadius(null);
    }
  }, [cityCenter?.latitude, cityCenter?.longitude, getCityRadiusFromCoordinates]);

  useEffect(() => {
    if (!placesLib) return;

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    const request: google.maps.places.AutocompleteRequest = {
      ...requestOptions,
      input: inputString,
      sessionToken: sessionTokenRef.current,
    };

    if (cityCenter?.latitude && cityCenter?.longitude) {
      const centerLat = Number(cityCenter.latitude);
      const centerLng = Number(cityCenter.longitude);

      const radius = dynamicRadius || DEFAULT_RADIUS;

      const latDelta = radius / 111000;
      const lngDelta = radius / (111000 * Math.cos((centerLat * Math.PI) / 180));

      request.locationRestriction = {
        north: centerLat + latDelta,
        south: centerLat - latDelta,
        east: centerLng + lngDelta,
        west: centerLng - lngDelta,
      };
    }

    if (inputString === '') {
      if (suggestions.length > 0) setSuggestions([]);
      return;
    }

    setIsLoading(true);
    AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((res) => {
      setSuggestions(res.suggestions);
      setIsLoading(false);
    });
  }, [placesLib, inputString, cityCenter, dynamicRadius]);

  return {
    suggestions,
    isLoading,
    resetSession: () => {
      sessionTokenRef.current = null;
      setSuggestions([]);
    },
  };
};
